<template>
    <div class="position-relative">
        <!-- Header and Filter Section -->
        <b-card class="mt-4">
            <b-row class="align-items-end">
                <!-- Programme Dropdown -->
                <b-col md="5">
                    <b-form-group label="Choose Programme (Scheme):" label-class="required-label mb-1" class="mb-0">
                        <b-form-select v-model="selectedProgrammeId" :options="programmeOptions" class="form-control-sm"
                            @change="onProgrammeChange" />
                    </b-form-group>
                </b-col>

                <!-- Subgroup Dropdown -->
                <b-col md="5">
                    <b-form-group :label="`Choose ${subgroupLabel}:`" label-class="required-label mb-1" class="mb-0">
                        <b-form-select v-model="selectedSubgroupId" :options="subgroupOptions" class="form-control-sm"
                            @change="onSubgroupChange" />
                    </b-form-group>
                </b-col>

                <!-- Go Button -->
                <b-col md="2">
                    <b-button variant="primary" size="sm" class="w-100 font-weight-medium" @click="fetchStudentFees"
                        :disabled="!selectedProgrammeId || !selectedSubgroupId">
                        Go
                    </b-button>
                </b-col>
            </b-row>
        </b-card>

        <!-- Student Table -->
        <div v-if="studentList.length">
            <!-- Error Alert -->
            <div v-if="formError" class="alert alert-danger mb-3">{{ formError }}</div>

            <!-- Update Status -->
            <span v-if="updationEnable" class="text-success font-weight-bold ml-2">
                Updation In Progress... <br />
            </span>

            <!-- Generate Ledger Section -->

            <div class="text-right mt-2">
               <b-button v-if="!ledgersGeneratedForAll && allCategoriesSelected && updateOldData" variant="success"
                    @click="generateLedger" :disabled="updationEnable">
                    Generate Ledger
                </b-button>
                <div v-if="ledgersGeneratedForAll" class="text-success font-weight-bold">
                    Ledger has been generated for all students.
                </div>
            </div>
            <br>

            <!-- Table with Students -->
                <table class="table table-striped table-bordered">
                    <thead class="thead-dark">
                        <tr>
                            <th>SI. No.</th>
                            <th>Student Name</th>
                            <th>Email</th>
                            <th>Mobile</th>
                            <th>
                                Fee Structure
                                <b-form-select v-model="bulkCategory" :options="categoryOptions"
                                    class="form-control-sm font-weight-normal mt-1" :disabled="allStudentsLocked"
                                    @change="applyBulkCategory" />
                            </th>
                            <th>Components</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(student, index) in studentList" :key="index">
                            <td>{{ index + 1 }}</td>
                            <td>{{ student.text }}</td>
                            <td>{{ student.email }}</td>
                            <td>{{ student.mobile }}</td>

                            <!-- Category Selector -->
                            <td class="d-flex align-items-center">
                                <div class="flex-grow-1 position-relative">
                                    <b-form-select v-model="student.category" :options="categoryOptions"
                                        class="form-control" :disabled="hasPaidTransaction(student)"
                                        @change="() => {
                                            updateOldData = false;
                                            $nextTick(() => onCategoryChange(student));
                                        }" />
                                    <span v-if="!student.category" class="text-muted">Not yet assigned - will be skipped on Update.</span>
                                </div>

                                <!-- Lock icon with tooltip -->
                                <div v-if="hasPaidTransaction(student)" class="ms-2 tooltip-wrapper">
                                    <b-icon icon="lock-fill" variant="danger"></b-icon>
                                    <div class="custom-tooltip">
                                        <span>Payment Received</span>
                                    </div>
                                </div>
                            </td>

                            <td>
                                {{ student.components }}

                                <span v-if="!student.category" class="text-muted d-block">
                                    No fee structure selected yet.
                                </span>
                            </td>

                        </tr>
                    </tbody>
                </table>
           

            <!-- Update Button -->
            <div class="text-right mt-3">
                <b-button variant="primary" v-if="!allStudentsLocked" :disabled="updationEnable"
                    @click="updateStudentFeeManagement">
                    Update
                </b-button>
            </div>
        </div>

        <div v-else>
            <div v-if="loading" class="d-flex justify-content-center mt-3">
                <span>Loading...</span>
            </div>
            <div v-else-if="submitted && selectedSubgroupId && studentList.length === 0" class="d-flex justify-content-center mt-3">
                <span class="text-danger">Students not found.</span>
            </div>
        </div>


    </div>
</template>

<script>
export default {
    data() {
        return {
            submitted: false,
            loading: false,
            // Selected dropdown values
            selectedProgrammeId: null,
            selectedSubgroupId: null,

            // Display label for subgroup
            subgroupLabel: '',

            // Options for dropdowns
            programmeOptions: [{ value: null, text: 'Select an option' }],
            subgroupOptions: [{ value: null, text: 'Select an option' }],
            categoryOptions: [],
            bulkCategory: null,

            // Student data
            studentList: [],
            studentFeeList: [],

            // Fee category data
            feeCategories: [],

            // Form state
            formError: '',
            updateOldData: true,
            updationEnable: false,
            allCategoriesSelected: false,
            ledgersGeneratedForAll: false,
            allStudentsLocked: false

        };
    },

    async created() {
        // Set subgroup label using custom naming from store
        this.subgroupLabel = this.$store.getters.getCustomNamings?.userSubgroup?.custom_name || 'Usersubgroup';

        // Load programmes and fee categories initially
        await this.loadProgrammes();
        await this.loadFeeCategories();
    },


    watch: {
        studentList: {
            handler(newVal) {
                if (this.updateOldData) {
                    newVal.forEach((student) => {
                        const feeData = this.studentFeeList[student.value];
                        if (feeData) {
                            // Only set values if category is not already selected manually
                            if (!student.category) {
                                student.fee_student_category_mapping_id = feeData.id;
                                student.transactions = feeData.transactions;
                                student.category = feeData.feecategory.id;
                                student.components = feeData.feecategory.components
                                    .map((c) => `${c.name}`)
                                    .join(', ');
                                student.component_amounts = feeData.feecategory.components
                                    .map((c) => `${c.amount}`)
                                    .join(', ');
                                student.amount = feeData.feecategory.components.reduce(
                                        (sum, c) => sum + (c.component_type === 'discount'
                                            ? -parseFloat(c.amount || 0)
                                            : parseFloat(c.amount || 0)),
                                        0
                                    );
                            }
                        }
                    });
                    this.allCategoriesSelected = this.studentList.every(student => student.category !== null && student.category !== undefined);
                    this.ledgersGeneratedForAll = this.studentList.every(student => student.transactions !== null && student.transactions.length > 0);
                    this.allStudentsLocked = this.studentList.length > 0 && this.studentList.every(student => this.hasPaidTransaction(student));
                }
            },
            deep: true,
        },
    },

    methods: {
        // A transaction row can be a debit-only entry (fee assigned, unpaid)
        // or a credit entry (actual payment). Only lock the category once
        // money has actually been paid, not merely when a ledger exists.
        hasPaidTransaction(student) {
            // Summed so a fully refunded payment no longer counts.
            return !!student.transactions && student.transactions.reduce(
                (total, t) => total + parseFloat(t.credit_amount || 0), 0
            ) > 0.0001;
        },

        async generateLedger() {

            this.formError = '';

            if (!this.ledgersGeneratedForAll && !this.allCategoriesSelected || !this.updateOldData) {
                this.formError = 'Please ensure all students have a fee structure selected and data is updated.';
                return;
            }

            this.updationEnable = true;

            try {
                const url = `${this.$store.getters.getAPIKey.mainAPI}/studentcategorymappings/generate-ledger`;
                const payload = {

                    students: this.studentList
                        .filter(student => !student.transactions || student.transactions.length === 0)
                        .map(student => ({
                            fee_student_category_mapping_id: student.fee_student_category_mapping_id,
                            institution_user_id: student.value,
                            amount: student.amount
                        })),

                };

                const response = await this.$axios.post(url, payload);

                if (response.data.success) {
                    this.$toast.success('Ledger generated successfully.', {
                        position: 'top',
                        duration: 4000,
                    });
                    await this.fetchStudentFees();
                }
            } catch (error) {
                error;
                this.formError = 'Failed to generate ledger. Please try again.';
            } finally {
                this.updationEnable = false;
            }
        },
        // Handle programme dropdown change
        onProgrammeChange() {
            this.selectedSubgroupId = null;
            this.studentList = [];
            this.studentFeeList = [];
            this.loadSubgroups();
        },

        // Handle subgroup change
        onSubgroupChange() {
            this.submitted = false;
            this.studentList = [];
            this.studentFeeList = [];
        },

        // Fetch student fee data and load student list
        async fetchStudentFees() {
            this.loading = true;
            const url = `${this.$store.getters.getAPIKey.mainAPI}/studentcategorymapping?subgroup_id=${this.selectedSubgroupId}`;
            try {
                const response = await this.$axios.get(url);
                this.studentFeeList = response.data;
                await this.loadStudents();
            } catch (error) {
                error;
            } finally {
                this.submitted = true; // Mark that the Go button was clicked, only after data has loaded
                this.loading = false;
            }
        },

        // Submit fee info for whichever students currently have a fee structure
        // selected - students left blank are simply skipped and can be set later.
        async updateStudentFeeManagement() {
            this.formError = '';

            const selected = this.studentList.filter((student) => student.category);
            if (!selected.length) {
                this.formError = 'Please select a fee structure for at least one student.';
                return;
            }

            this.updationEnable = true;

            const url = `${this.$store.getters.getAPIKey.mainAPI}/studentcategorymapping`;
            const payload = selected.map((student) => ({
                institution_user_id: student.value,
                fee_category_id: student.category,
                fee_student_category_mapping_id: student.fee_student_category_mapping_id || null,
            }));

            try {
                await this.$axios.post(url, { data: payload });
                this.$toast.success('Student fee data submitted successfully.', {
                    position: 'top',
                    duration: 4000,
                });
                this.updateOldData = true;
                await this.fetchStudentFees();
            } catch (error) {
                this.formError = 'Failed to submit. Please try again.';
            } finally {
                this.updationEnable = false;
            }
        },

        // Load programme options
        async loadProgrammes() {
            const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups?allusergroups=1`;
            const response = await this.$axios.get(url);
            this.programmeOptions = [
                { value: null, text: 'Select an option' },
                ...response.data.map((programme) => ({
                    value: programme.id,
                    text: `${programme.name} (${programme.code}) ${programme.scheme || ''}`,
                })),
            ];
        },

        async getDefaultAcademicYear() {
            // $root.default_academic_year is only ever populated as a side effect of
            // visiting the Staff List / Set Academic Year pages, not during app
            // bootstrap - fetch it directly instead of trusting it's already set,
            // otherwise this silently queries academic_year=null and returns nothing.
            if (this.$root.default_academic_year[0]) return this.$root.default_academic_year[0];

            const url = `${this.$store.getters.getAPIKey.mainAPI}/user/setting/default_academic_year`;
            const response = await this.$axios.get(url);
            this.$root.default_academic_year[0] = `${response.data}`;
            return this.$root.default_academic_year[0];
        },

        // Load subgroup options for selected programme
        async loadSubgroups() {
            try {
                const academicYear = await this.getDefaultAcademicYear();
                const url = `${this.$store.getters.getAPIKey.mainAPI}/usergroups/${this.selectedProgrammeId}/usersubgroups?academic_year=${academicYear}`;
                const response = await this.$axios.get(url);
                this.subgroupOptions = [
                    { value: null, text: 'Select an option' },
                    ...response.data
                        .filter(subgroup => subgroup.type === null)
                        .map(subgroup => ({
                            value: subgroup.id,
                            text: `${subgroup.name} (${subgroup.code})`
                        }))
                ];
            } catch (error) {
                error;
                this.subgroupOptions = [{ value: null, text: 'Error loading options' }];
            }
        },

        // Load student users from selected subgroup
        async loadStudents() {
            const url = `${this.$store.getters.getAPIKey.mainAPI}/usersubgroup/${this.selectedSubgroupId}/users`;
            const response = await this.$axios.get(url);
            this.studentList = response.data
                .filter((user) => user.pivot.usersubgrouprole_id === 2)
                .map((user) => ({
                    value: user.pivot.institution_user_id,
                    text: `${user.first_name} ${user.last_name}`,
                    email: user.email,
                    mobile: user.mobile,
                    transactions: [],
                    category: null,
                    components: '',
                    amount: '',
                }));
        },

        // Load fee categories with components
        async loadFeeCategories() {
            try {
                const url = `${this.$store.getters.getAPIKey.mainAPI}/feecategories?with_components=1`;
                const response = await this.$axios.get(url);
                this.feeCategories = response.data;
                this.categoryOptions = [
                    { value: null, text: 'Select Fee Structure' },
                    ...this.feeCategories.map((cat) => ({
                        value: cat.id,
                        text: cat.name,
                    })),
                ];
            } catch (error) {
                error;
            }
        },

        // Apply selected fee structure to every student that isn't locked by a paid transaction
        applyBulkCategory() {
            if (!this.bulkCategory) return;
            this.updateOldData = false;
            this.studentList.forEach((student) => {
                if (!this.hasPaidTransaction(student)) {
                    student.category = this.bulkCategory;
                    this.onCategoryChange(student);
                }
            });
        },

        // On fee category change for student
        onCategoryChange(student) {
            const selected = this.feeCategories.find((cat) => cat.id === student.category);
            console.log("selected",selected);
            if (selected) {
                student.components = selected.components
                    .map((c) => `${c.name}`)
                    .join(', ');
                student.component_amounts = selected.components
                    .map((c) => `${c.amount}`)
                    .join(', ');
                student.amount = selected.components.reduce(
                    (sum, c) => sum + (c.component_type === 'discount'
                        ? -parseFloat(c.amount || 0)
                        : parseFloat(c.amount || 0)),
                    0
                );
            } else {
                student.components = '';
                student.amount = '';
            }
            this.allCategoriesSelected = this.studentList.every(student => student.category !== null && student.category !== undefined);
            this.ledgersGeneratedForAll = this.studentList.every(student => student.transactions !== null && student.transactions.length > 0);
            this.allStudentsLocked = this.studentList.length > 0 && this.studentList.every(student => this.hasPaidTransaction(student));
        },
    },
};
</script>

<style scoped>
/* Red asterisk for required labels */
.required-label::after {
    content: '*';
    color: red;
    margin-left: 4px;
}

.tooltip-wrapper {
    position: relative;
    display: inline-block;
    cursor: pointer;
}

.custom-tooltip {
    visibility: hidden;
    background-color: #000;
    color: #fff;
    padding: 6px 10px;
    border-radius: 6px;
    position: absolute;
    z-index: 999;
    top: 100%;
    /* position below */
    left: 0;
    white-space: nowrap;
    opacity: 0;
    transition: opacity 0.3s;
    margin-top: 5px;
    font-size: 13px;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.2);
}

.tooltip-wrapper:hover .custom-tooltip {
    visibility: visible;
    opacity: 1;
}
</style>
